import { useState, useEffect } from "preact/hooks";
import { EMOJI_DISPLAY, tagLabelClass } from "../state";
import { putEvent } from "../api";
import { BrutalButton, BrutalSelect, BrutalInput } from "./BrutalUI";
import type { Slot } from "../state";

const EMOJIS = ["heart", "laugh", "thumbs_up", "thumbs_down"] as const;

interface SlotRowProps {
  slot: Slot;
  slug: string;
  username: string;
  currentUserEmoji?: string;
  reactionsByUser?: Record<string, string>;
  existingTags?: { id: number; name: string }[];
}

function votersForEmoji(
  reactionsByUser: Record<string, string> | undefined,
  emoji: string,
): string {
  if (!reactionsByUser) return "";
  return Object.entries(reactionsByUser)
    .filter(([, e]) => e === emoji)
    .map(([u]) => u)
    .join(", ");
}

export default function SlotRow({
  slot,
  slug,
  username,
  currentUserEmoji = "",
  reactionsByUser = {},
  existingTags = [],
}: SlotRowProps) {
  const [tagInput, setTagInput] = useState("");
  const [showTagDialog, setShowTagDialog] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [desc, setDesc] = useState(slot.description ?? "");
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const counts = slot.reaction_counts || {};

  useEffect(() => {
    if (!isEditingDesc) setDesc(slot.description ?? "");
  }, [slot.description, isEditingDesc]);
  const current = currentUserEmoji;

  const addTag = (e: Event) => {
    e.preventDefault();
    const name = tagInput.trim();
    if (!name) return;
    const existing = existingTags.find(
      (t) => t.name.toLowerCase() === name.toLowerCase(),
    );
    const tagId = existing ? existing.id : Date.now();
    const tagName = existing ? existing.name : name;
    if (!existing) {
      putEvent(slug, {
        type: "tag_created",
        message: `${username} created tag "${tagName}"`,
        data: { tag_id: tagId, name: tagName },
      });
    }
    putEvent(slug, {
      type: "tag_added_to_slot",
      message: `${username} added tag "${tagName}" to slot`,
      data: { slot_id: slot.id, tag_id: tagId, tag_name: tagName },
    });
    setTagInput("");
  };

  const submitReaction = (emoji: string) => (e: Event) => {
    e.preventDefault();
    const disp = EMOJI_DISPLAY[emoji] || emoji;
    putEvent(slug, {
      type: "reaction_set",
      message: `${username} reacted ${disp} on ${slot.date} ${slot.start_time}–${slot.end_time}`,
      data: { slot_id: slot.id, user_id: 0, emoji, username },
    });
  };

  const clearReaction = (e: Event) => {
    e.preventDefault();
    putEvent(slug, {
      type: "reaction_cleared",
      message: `${username} cleared reaction on slot`,
      data: { slot_id: slot.id, user_id: 0, username, emoji: current },
    });
  };

  const removeTag = (tagId: number, tagName: string) => (e: Event) => {
    e.preventDefault();
    putEvent(slug, {
      type: "tag_removed_from_slot",
      message: `${username} removed label "${tagName}"`,
      data: { slot_id: slot.id, tag_id: tagId, tag_name: tagName },
    });
  };

  const saveDescription = (e: Event) => {
    e.preventDefault();
    const trimmed = desc.trim();
    if (trimmed === (slot.description ?? "")) {
      setIsEditingDesc(false);
      return;
    }
    putEvent(slug, {
      type: "slot_description_updated",
      message: `${username} edited the description for ${slot.date} ${slot.start_time}–${slot.end_time}`,
      data: { slot_id: slot.id, date: slot.date, description: trimmed },
    });
    setIsEditingDesc(false);
  };

  const handleSelectTag = (e: Event) => {
    const target = e.target as HTMLSelectElement;
    const value = target.value;
    if (!value) return;
    if (value === "__new__") {
      setShowTagDialog(true);
      target.value = "";
      return;
    }
    const tagId = Number(value);
    const existing = existingTags.find((t) => t.id === tagId);
    if (existing) {
      putEvent(slug, {
        type: "tag_added_to_slot",
        message: `${username} added tag "${existing.name}" to slot`,
        data: {
          slot_id: slot.id,
          tag_id: existing.id,
          tag_name: existing.name,
        },
      });
    }
    target.value = "";
  };

  const handleCreateTag = (e: Event) => {
    e.preventDefault();
    const name = newTagName.trim();
    if (!name) return;
    const existing = existingTags.find(
      (t) => t.name.toLowerCase() === name.toLowerCase(),
    );
    let tagId = existing?.id ?? Date.now();
    const tagName = existing?.name ?? name;
    if (!existing) {
      putEvent(slug, {
        type: "tag_created",
        message: `${username} created tag "${tagName}"`,
        data: { tag_id: tagId, name: tagName },
      });
    }
    putEvent(slug, {
      type: "tag_added_to_slot",
      message: `${username} added tag "${tagName}" to slot`,
      data: { slot_id: slot.id, tag_id: tagId, tag_name: tagName },
    });
    setNewTagName("");
    setShowTagDialog(false);
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        className="p-4 mb-3 border-2 border-black dark:border-gray-100 bg-orange-50 dark:bg-gray-900 flex flex-col gap-2"
        data-slot-id={slot.id}
      >
        <div className="flex flex-wrap gap-2 items-center">
          <div className="font-semibold text-brutal-purple dark:text-brutal-purple-hover">
            {slot.start_time}–{slot.end_time}
          </div>
          <div className="text-sm opacity-85">{slot.creator_name}</div>
          <div className="flex-1 min-w-0" />
        </div>
        <div className="w-full">
          {isEditingDesc ? (
            <form
              onSubmit={saveDescription}
              className="flex flex-col gap-1 font-mono"
            >
              <textarea
                value={desc}
                onInput={(e) =>
                  setDesc((e.target as HTMLTextAreaElement).value)
                }
                placeholder="Description"
                rows={4}
                className="w-full px-3 py-2 text-sm border-2 border-black dark:border-gray-100 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 resize-y min-h-[4rem]"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-3 py-1.5 text-sm font-semibold rounded-none border-2 border-black dark:border-gray-100 bg-brutal-orange text-black dark:text-gray-900 hover:bg-brutal-orange-hover"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDesc(slot.description ?? "");
                    setIsEditingDesc(false);
                  }}
                  className="px-3 py-1.5 text-sm rounded-none border-2 border-black dark:border-gray-100 bg-white dark:bg-gray-950 hover:bg-gray-200 dark:hover:bg-gray-800"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="flex items-start gap-2 font-mono">
              <div className="flex-1 min-w-0 text-sm text-black dark:text-gray-100 whitespace-pre-wrap">
                {slot.description || "—"}
              </div>
              <BrutalButton
                type="button"
                variant="secondary"
                className="px-2 py-1 text-xs min-h-0 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 shrink-0"
                onClick={() => setIsEditingDesc(true)}
              >
                Edit
              </BrutalButton>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 items-center mt-2">
          {(slot.tags || []).map((t) => (
            <button
              key={t.tag_id}
              type="button"
              onClick={removeTag(t.tag_id, t.tag_name)}
              title="Press to remove"
              aria-label={`Remove label ${t.tag_name}`}
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-sm font-semibold border-2 border-black dark:border-gray-100 ${tagLabelClass(t.tag_name)}`}
            >
              {t.tag_name}
              <span className="opacity-90 ml-1" aria-hidden>
                ×
              </span>
            </button>
          ))}
          <BrutalSelect
            value=""
            onChange={handleSelectTag}
            className="text-xs min-w-[7rem]"
          >
            <option value="">
              {(slot.tags || []).length ? "Add label" : "Add label…"}
            </option>
            {existingTags.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
            <option value="__new__">+ Create new label…</option>
          </BrutalSelect>
        </div>
        {showTagDialog && (
          <div className="brutal-modal-overlay flex items-center justify-center">
            <div className="border-2 border-black dark:border-gray-100 bg-white dark:bg-gray-950 p-4 max-w-xs w-[90%] flex flex-col gap-3">
              <div className="text-sm font-semibold">Create new label</div>
              <BrutalInput
                value={newTagName}
                onInput={(e) =>
                  setNewTagName((e.target as HTMLInputElement).value)
                }
                placeholder="Label name"
                className="w-full"
              />
              <div className="flex gap-2 justify-end">
                <BrutalButton
                  type="button"
                  variant="primary"
                  className="px-3 py-1 text-xs min-h-0"
                  onClick={handleCreateTag}
                >
                  Save
                </BrutalButton>
                <BrutalButton
                  type="button"
                  variant="secondary"
                  className="px-3 py-1 text-xs min-h-0"
                  onClick={() => {
                    setShowTagDialog(false);
                    setNewTagName("");
                  }}
                >
                  Cancel
                </BrutalButton>
              </div>
            </div>
          </div>
        )}
        <div className="flex gap-2 mt-2">
          {EMOJIS.map((emoji) => {
            const voters = votersForEmoji(reactionsByUser, emoji);
            return (
              <button
                key={emoji}
                type="button"
                className={`px-1.5 py-0.5 text-lg min-w-[2rem] border-2 border-black dark:border-gray-100 ${
                  current === emoji
                    ? "bg-brutal-purple text-white"
                    : "bg-white dark:bg-gray-950"
                }`}
                title={
                  voters
                    ? `${EMOJI_DISPLAY[emoji] || emoji} · ${voters}`
                    : emoji
                }
                onClick={
                  current === emoji ? clearReaction : submitReaction(emoji)
                }
              >
                {EMOJI_DISPLAY[emoji] || emoji} {counts[emoji] || 0}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
